import { motion } from 'framer-motion'
import { Button, Badge } from '../components/primitives/index.js'
import { HERO_SOCIAL_PROOF, HERO_INFO_PANEL, FEATURE_BAR } from '../config/site.js'
import { useProducts } from '../context/ProductsContext.jsx'
import heroVialImg from '../assets/bg.webp'
import './Hero.css'

const EASE = [0.16, 1, 0.3, 1]

/**
 * Field names mirror Prod's Hero.jsx HERO_DEFAULTS exactly (badge/titleLine1/
 * titleAccent/lead/cta1-2) so an admin-managed siteContent.hero payload drops
 * straight in — see ProductsContext.siteContent.hero.
 */
const HERO_DEFAULTS = {
  badge: 'Research. Purity. Results.',
  titleLine1: 'Premium Peptides.',
  titleAccent: 'Proven Results.',
  lead: 'High-purity peptides. Backed by science. Trusted by those who demand more.',
  cta1Text: 'Shop Best Sellers',
  cta1Link: '/#best-sellers',
  cta2Text: 'Explore Peptides',
  cta2Link: '/catalog',
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
}

const ICONS = {
  flask: FlaskIcon,
  shield: ShieldIcon,
  lock: LockIcon,
  doc: DocIcon,
  globe: GlobeIcon,
  badge: BadgeIcon,
  'shield-check': ShieldCheckIcon,
}

export default function Hero() {
  const { siteContent } = useProducts()
  const h = { ...HERO_DEFAULTS, ...(siteContent?.hero ?? {}) }
  const socialProof = { ...HERO_SOCIAL_PROOF, ...(siteContent?.hero?.socialProof ?? {}) }
  const infoPanel = siteContent?.hero?.infoPanel?.length ? siteContent.hero.infoPanel : HERO_INFO_PANEL
  const featureBar = siteContent?.hero?.featureBar?.length ? siteContent.hero.featureBar : FEATURE_BAR

  return (
    <section className="hero">
      <div className="hero__atmosphere" aria-hidden="true">
        <div className="hero__vignette" />
      </div>
      <div className="container hero__inner">
        <motion.div className="hero__copy" initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } }}>
          <motion.div variants={fadeUp}>
            <Badge tone="neutral" className="hero__badge">
              {h.badge}
            </Badge>
          </motion.div>

          <motion.h1 variants={fadeUp} className="hero__title">
            <span className="hero__title-line">{h.titleLine1}</span>
            <span className="hero__title-line">{h.titleAccent}</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="hero__subtitle">
            {h.lead}
          </motion.p>

          <motion.div variants={fadeUp} className="hero__actions">
            <Button to={h.cta1Link} variant="primary" size="lg" className="hero__cta">
              {h.cta1Text} &rarr;
            </Button>
            <Button to={h.cta2Link} variant="ghost" size="lg" className="hero__cta hero__cta--ghost">
              {h.cta2Text}
            </Button>
          </motion.div>

          <motion.div variants={fadeUp} className="hero__proof">
            <div className="hero__avatars">
              {socialProof.avatars.map((a, i) => {
                const style = {
                  zIndex: socialProof.avatars.length - i,
                  ...(a.image ? { backgroundImage: `url(${a.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                }
                return a.link ? (
                  <a key={i} href={a.link} target="_blank" rel="noopener noreferrer" className="hero__avatar" style={style} />
                ) : (
                  <span key={i} className="hero__avatar" style={style} />
                )
              })}
            </div>
            <div className="hero__proof-text">
              <span className="hero__stars" aria-hidden="true">
                ★★★★★
              </span>
              <span className="hero__proof-rating">{socialProof.rating}</span>
            </div>
          </motion.div>
          <motion.p variants={fadeUp} className="hero__proof-reviewers">
            {socialProof.reviewers}
          </motion.p>
        </motion.div>

        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        >
          <div className="hero__visual-frame">
            <img src={h.vialImage || heroVialImg} alt="LooksLab research vial" className="hero__visual-img" draggable={false} fetchPriority="high" />
            <div className="hero__visual-scrim" />
          </div>

          <div className="hero__info-panel">
            {infoPanel.map((item) => {
              const Icon = ICONS[item.icon]
              return (
                <div key={item.title} className="hero__info-row">
                  <Icon />
                  <div>
                    <span className="hero__info-title">{item.title}</span>
                    <span className="hero__info-subtitle">{item.subtitle}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>
      </div>

      <div className="container hero__feature-bar">
        {featureBar.map((item, i) => {
          const Icon = ICONS[item.icon]
          return (
            <motion.div
              key={item.title}
              className="hero__feature"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
            >
              <Icon />
              <div>
                <span className="hero__feature-title">{item.title}</span>
                <span className="hero__feature-subtitle">{item.subtitle}</span>
              </div>
            </motion.div>
          )
        })}
      </div>
    </section>
  )
}

function FlaskIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M9 3h6M10 3v6l-5.5 9.5A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-2.5L14 9V3" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 2 4 5v6c0 5 3.4 8.4 8 11 4.6-2.6 8-6 8-11V5l-8-3Z" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  )
}

function DocIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </svg>
  )
}

function GlobeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 4 6 4 9s-1.5 6.4-4 9c-2.5-2.6-4-6-4-9s1.5-6.4 4-9Z" />
    </svg>
  )
}

function BadgeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="9" r="6" />
      <path d="m8.5 14-1.5 7 5-3 5 3-1.5-7" />
    </svg>
  )
}

function ShieldCheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 2 4 5v6c0 5 3.4 8.4 8 11 4.6-2.6 8-6 8-11V5l-8-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}
